import type { ReactNode } from "react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { GapDocState } from "../api/encounters";

export const STATE_LABELS: Record<Exclude<GapDocState, "present">, string> = {
  missing_feed: "Missing (feed)",
  awaiting_scrape_not_triggerable: "Awaiting scrape — not triggerable",
  awaiting_scrape_triggerable: "Awaiting scrape — not returned",
};

export const STATE_BAR_COLORS: Record<Exclude<GapDocState, "present">, string> = {
  missing_feed: "bg-amber-500",
  awaiting_scrape_not_triggerable: "bg-orange-500",
  awaiting_scrape_triggerable: "bg-rose-500",
};

export const MISSING_STATES = Object.keys(STATE_LABELS) as Array<Exclude<GapDocState, "present">>;

export const STATE_DESCRIPTIONS: Record<GapDocState, string> = {
  present: "The required document is present — this encounter satisfies this requirement.",
  missing_feed:
    "A required document that comes from the primary data feed, and it hasn't arrived. The fix is to chase the source/feed system.",
  awaiting_scrape_not_triggerable:
    "A required document supplied by the RPA scrape, but the scrape's preconditions (the feed documents that must arrive before a scrape is fired) aren't present yet — so no scrape has been triggered. Chase the precondition feed documents.",
  awaiting_scrape_triggerable:
    "A required document supplied by the RPA scrape: its preconditions are met (a scrape should have fired) but the document still hasn't come back. Investigate the scrape — pending, failed, or it returned nothing.",
};

export const SOURCE_DESCRIPTIONS: Record<"feed" | "scrape", string> = {
  feed: "This document arrives via the primary data feed.",
  scrape: "This document is expected from the RPA scrape.",
};

/** Wraps content in a hover tooltip with a dotted underline to signal an explanation is available. */
export function GapHint({ children, description }: { children: ReactNode; description: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help underline decoration-dotted underline-offset-2">{children}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {description}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
